import { HttpHelper, Utils, AppContext, BaseModel, TypeOf, DependencyContainer } from '../../..'
import {
  Store,
  ModelMapper,
  EntityMappingManager,
  EntityMappingFactory,
  OperationServiceFactory,
  ModelIdentityMap,
  JsonapiUrlGenerator,
  IOperationServiceConfig,
} from '../..'

import { JsonApiOperationRequest, JsonApiProcessor, OperationServiceAdapter } from '../../../data/services'
import { ModelFactory } from '../../../data/factories'
import { UriFactory } from '../../../core/factories'
import { ServiceBuilder, IServiceBuilderResult } from '../service-builder'

export class OperationServiceBuilder
  extends ServiceBuilder<OperationServiceAdapter<BaseModel>, IOperationServiceConfig<BaseModel>> {

  constructor(
    private _apiRoot: string,
    private _httpHelper: HttpHelper,
    _utils: Utils,
    private _appContext: AppContext,
    private _entityMappingFactory: EntityMappingFactory,
    private _uriFactory: UriFactory,
    _dependencyContainer: DependencyContainer,
    _idMap: ModelIdentityMap
  ) {
    super(_utils, _dependencyContainer, _idMap)
  }

  public build(): Array<IServiceBuilderResult<OperationServiceAdapter<BaseModel>>> {
    if (this._configs.size === 0) {
      throw new Error('No configuration provided!')
    }

    const factory = this._createOperationServiceFactory()
    return this._utils.mapSet(
      this._configs,
      (config) => this._buildFromConfig(config, factory)
    )
  }

  public buildClass<S extends OperationServiceAdapter<BaseModel>>(serviceKlass: TypeOf<S>): IServiceBuilderResult<S> {
    const config = this._utils.findSet(this._configs, (c) => c.service === serviceKlass)
    if (!config) { throw new Error(`No configuration for '${serviceKlass.name}' provided!`) }

    return this._buildFromConfig(config, this._createOperationServiceFactory())
  }

  private _buildFromConfig<S extends OperationServiceAdapter<BaseModel>>(
    config: IOperationServiceConfig<BaseModel>, factory: OperationServiceFactory
  ): IServiceBuilderResult<S> {
      const instance = factory.create(config.service)
      return { class: config.service, instance: instance } as IServiceBuilderResult<S>
  }

  private _createOperationServiceFactory() {
    return new OperationServiceFactory(
      this._createJsonApiProcessor(),
      this._getServiceFactoryMethods()
    )
  }

  private _createJsonApiProcessor(): any {
    const entityMappingManager = this._createEntityMappingManager()
    const modelMapper = this._createModelMapper(entityMappingManager)
    return new JsonApiProcessor(
      this._httpHelper,
      this._createStore(),
      modelMapper,
      this._utils,
      this._createOperationRequest(modelMapper, entityMappingManager)
    )
  }

  private _createOperationRequest(modelMapper: ModelMapper, entityMappingManager: EntityMappingManager): any {
    return new JsonApiOperationRequest(
      this._utils,
      this._createJsonApiUrlGenerator(entityMappingManager),
      modelMapper,
      this._uriFactory,
    )
  }

  private _createJsonApiUrlGenerator(entityMappingManager: EntityMappingManager) {
    return new JsonapiUrlGenerator(
      this._apiRoot,
      this._getEndpointMapping(),
      entityMappingManager,
      this._appContext,
    )
  }

  private _createModelMapper(entityMappingManager: EntityMappingManager): any {
    return new ModelMapper(
      this._createOrGetIdentityMap(),
      this._createModelFactory(),
      entityMappingManager,
      this._utils
    )
  }

  private _createStore(): Store {
    return new Store()
  }

  private _createEntityMappingManager(): EntityMappingManager {
    return new EntityMappingManager(
      this._getEntityMappingConfigs(),
      this._entityMappingFactory,
      this._utils
    )
  }

  private _createModelFactory(): ModelFactory {
    return new ModelFactory(this._getModelClasses(), this._utils)
  }

  private _createOrGetIdentityMap(): ModelIdentityMap {
    return this._idMap || new ModelIdentityMap()
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/creators/operation-service-builder/operation-service.builder.ts